import { NavArrowRightIcon } from '@synoptic/ui-kit/icons/react/nav-arrow-right.js';
import { SynopticLogoIcon } from '@synoptic/ui-kit/icons/react/synoptic-logo.js';
import { useIsLaptop } from '@synoptic/ui-kit/responsive/hooks.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@synoptic/ui-kit/tooltip/tooltip.js';
import { clsx } from 'clsx';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { useSidebarContext } from '../sidebar/sidebar-context.tsx';
import { useAuthInfo } from '../use-auth-info.ts';
import { NavItemByIdV2 } from './nav-item-by-id-v2.tsx';
import { navTooltipArrow, navTooltipContent } from './nav-item-v2.css.ts';
import { NavSectionV2 } from './nav-item-v2.tsx';
import { sidebarSectionsV2, visibleNavItemsV2 } from './nav-items-v2.tsx';
import { NavVariantV2Provider } from './nav-variant-v2.ts';
import { ProfileMenuV2 } from './profile-menu-v2.tsx';
import {
  collapseEdge,
  collapseToggle,
  collapseToggleIconExpanded,
  logoLink,
  nav,
  sidebar,
  sidebarScroll,
} from './sidebar-v2.css.ts';

const CollapseToggleV2 = ({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) => {
  const label = collapsed ? 'Expand menu' : 'Collapse menu';

  return (
    <Tooltip disableHoverableContent>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={collapseToggle}
          aria-label={label}
          onClick={onToggle}
        >
          <NavArrowRightIcon
            className={clsx({ [collapseToggleIconExpanded]: !collapsed })}
          />
        </button>
      </TooltipTrigger>
      <TooltipContent
        color="prominent"
        side="right"
        className={navTooltipContent}
        arrowClassName={navTooltipArrow}
        description={label}
      />
    </Tooltip>
  );
};

export const SidebarV2 = () => {
  const isLaptop = useIsLaptop();
  const { isCollapsed, setIsCollapsed } = useSidebarContext();
  const { isAdmin } = useAuthInfo();

  const variant = isCollapsed || !isLaptop ? 'rail' : 'expanded';

  return (
    <NavVariantV2Provider value={variant}>
      <aside className={sidebar} data-variant={variant}>
        <div className={sidebarScroll}>
          <Link to={paths.home} className={logoLink} aria-label="Synoptic">
            <SynopticLogoIcon width={107} height={29} />
          </Link>
          <nav className={nav} aria-label="Main">
            {sidebarSectionsV2.map((section) => {
              const items = visibleNavItemsV2(section.items, isAdmin);
              if (items.length === 0) {
                return null;
              }

              return (
                <NavSectionV2 key={section.id} label={section.label}>
                  {items.map((id) => (
                    <NavItemByIdV2 key={id} id={id} />
                  ))}
                </NavSectionV2>
              );
            })}
          </nav>
          <ProfileMenuV2 />
        </div>
        {isLaptop ? (
          <>
            <div
              className={collapseEdge}
              data-collapsed={isCollapsed ? '' : undefined}
              data-testid="sidebar-collapse-edge-v2"
              onClick={() => setIsCollapsed((prev) => !prev)}
            />
            <CollapseToggleV2
              collapsed={isCollapsed}
              onToggle={() => setIsCollapsed((prev) => !prev)}
            />
          </>
        ) : null}
      </aside>
    </NavVariantV2Provider>
  );
};
